import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserCertificate } from '@app/model/user-certificate.model';
import { combineLatest, Observable, of } from 'rxjs';
import { HttpService } from 'library-explorer';
import { DataResponse } from '@app/model/data-response.model';
import { NotificationModel } from '@app/model/notification.model';
import { map, mergeMap } from 'rxjs/operators';
import { Achievement } from '@app/model/achievement';
import { LeaderboardNotification } from '@app/model/leaderboard-notification';
import { MatDialog } from '@angular/material/dialog';
import { RewardModalComponent } from '@app/shared/components/reward-modal/reward-modal.component';
import { SettingsService } from './settings.service';

@Injectable({
  providedIn: 'root'
})
export class RewardNotificationService {

  constructor(
    private readonly settingsService: SettingsService,
    private readonly httpService: HttpService,
    private readonly dialog: MatDialog
  ) { }

  public checkRewards(showFeedback: boolean = false, lessonId?: string): void {
    this.getAllNotifications().subscribe(([certificates, achievements, points]) => {
 
      if (!showFeedback && !certificates?.length && !achievements?.length && !points.length) {
        return;
      }

      if (showFeedback && !lessonId) {
        throw new Error('Lesson id is required when showFeedback is true');
      }

      this.dialog.open(RewardModalComponent, {
        autoFocus: false,
        panelClass: ['mobile-full-width-dialog', 'dialog-no-padding'],
        data: {
          certificates,
          achievements,
          points,
          showFeedback,
          lessonId
        }
      });
    });
  }

  private getAllNotifications(): Observable<[UserCertificate[], Achievement[], LeaderboardNotification[]]> {
    return this.settingsService.getSettings()
      .pipe(
        mergeMap(settings => {
          return combineLatest([
            settings.features?.certificates ? this.getCertificationNotifications() : of([]),
            settings.features?.achievements ? this.getAchievementsNotifications() : of([]),
            settings.features?.leaderboard ? this.getLeaderboardsNotifications() : of([]),
          ]);
        })
      )

  }

  private getCertificationNotifications(status: number = 0): Observable<UserCertificate[]> {
    const params = new HttpParams().set('status', status.toString());
    return this.httpService.get<DataResponse<NotificationModel<UserCertificate>>>('notifications/certificate', params)
      .pipe(
        map(data => Array.isArray(data?.items) ? data.items.map(item => item.relatedEntity) : [])
      );
  }

  private getAchievementsNotifications(status: number = 0): Observable<Achievement[]> {
    const params = new HttpParams().set('status', `${status}`);
    return this.httpService.get<DataResponse<NotificationModel<Achievement>>>('notifications/achievement', params)
      .pipe(
        map(data => data.items.map(item => item.relatedEntity))
      );
  }
  
  private getLeaderboardsNotifications(status: number = 0): Observable<LeaderboardNotification[]> {
    const params = new HttpParams().set('status', `${status}`);
    return this.httpService.get<DataResponse<LeaderboardNotification>>('notifications/leaderboard_points', params)
      .pipe(
        map(data => data.items)
      );
  }
}
